import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  HStack,
  VStack,
  Heading,
  Image,
  Input,
  Text,
  useToast,
  Link,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  PinInput,
  PinInputField,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import useStore from '../../../store/store';
import axios from 'axios';

export const SigninModal = () => {
  const [loading, setLoading] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // States for form
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  
  // OTP States
  const [loginMethod, setLoginMethod] = useState('password');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);

  const { signin, setSignin, setUser } = useStore();
  const toast = useToast();

  const onClose = () => {
    setSignin(false);
    setLoading(false);
    setLoginMethod('password');
    setIsOtpSent(false);
    setOtp('');
  };

  const handleSendOtp = async () => {
    if (!phone || phone.length !== 10) {
      toast({ title: 'Please enter a valid 10-digit phone number', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_SERVER || 'http://localhost:5000/api';
      const response = await axios.post(`${API_URL}/send-otp`, { phone, role: 'user' });
      
      if (response.data.success) {
        setIsOtpSent(true);
        toast({ title: 'OTP sent successfully!', status: 'success', duration: 3000, isClosable: true });
        if (response.data.otp) console.log("OTP for testing:", response.data.otp);
      }
    } catch (error) {
      toast({ title: error?.response?.data?.message || 'Failed to send OTP', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();

    if (loginMethod === 'otp' && !isOtpSent) {
      handleSendOtp();
      return;
    }

    if (!phone) {
      toast({ title: 'Please enter phone number', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    
    if (phone.length !== 10) {
      toast({ title: 'Phone number must be exactly 10 digits', status: 'error', duration: 3000, isClosable: true });
      return;
    }

    try {
      setLoading(true);
      let response;
      const API_URL = process.env.REACT_APP_SERVER || 'http://localhost:5000/api';

      if (isSignup) {
        if (!firstName || !email || !password) {
          toast({ title: 'Please fill all required fields', status: 'error', duration: 3000, isClosable: true });
          setLoading(false);
          return;
        }
        response = await axios.post(`${API_URL}/register`, {
          firstName,
          lastName,
          email,
          phone,
          password,
          dob: new Date().toISOString()
        });
      } else if (loginMethod === 'password') {
        if (!password) {
          toast({ title: 'Please enter password', status: 'error', duration: 3000, isClosable: true });
          setLoading(false);
          return;
        }
        response = await axios.post(`${API_URL}/login`, { phone, password, role: 'user' });
      } else {
        // OTP Login
        if (!otp) {
          toast({ title: 'Please enter OTP', status: 'error', duration: 3000, isClosable: true });
          setLoading(false);
          return;
        }
        response = await axios.post(`${API_URL}/verify-otp`, { phone, otp, role: 'user' });
      }

      setUser(response?.data?.user);
      localStorage.setItem('user', JSON.stringify(response?.data?.user));
      
      toast({
        title: isSignup ? 'Account created successfully!' : 'Logged in successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: error?.response?.data?.message || 'Authentication failed',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      console.error("Auth Exception:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(value);
  };

  return (
    <Modal isOpen={signin} onClose={onClose} size={['md', 'lg', '2xl', '3xl']} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        <ModalHeader></ModalHeader>
        <ModalCloseButton />
        <ModalBody py={8}>
          <HStack alignItems={'center'} spacing={5}>
            <VStack flex={1} p={5} display={['none', 'none', 'none', 'flex']}>
              <Heading fontSize={'2xl'} textAlign="center">Your Health Partner</Heading>
              <Text color="gray.500" textAlign="center" mb={4}>Access your reports, book tests and manage your family health</Text>
              <Image src={'/assets/signup/Signup.png'} width={80} />
            </VStack>
            
            <VStack flex={1} p={[1, 5]} spacing={5} as="form" onSubmit={handleAuth} align="stretch">
              <Heading fontSize={'26'} textAlign="center" color="teal.700">
                {isSignup ? 'Create Account' : 'Welcome Back'}
              </Heading>

              {isSignup && (
                <>
                  <HStack>
                    <FormControl isRequired>
                      <FormLabel fontSize="xs" fontWeight="bold" color="gray.600">First Name</FormLabel>
                      <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" bg="gray.50" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs" fontWeight="bold" color="gray.600">Last Name</FormLabel>
                      <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" bg="gray.50" />
                    </FormControl>
                  </HStack>
                  <FormControl isRequired>
                    <FormLabel fontSize="xs" fontWeight="bold" color="gray.600">Email Address</FormLabel>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" bg="gray.50" />
                  </FormControl>
                </>
              )}

              <FormControl isRequired>
                <FormLabel fontSize="xs" fontWeight="bold" color="gray.600">Phone Number</FormLabel>
                <Input 
                  type="tel" 
                  value={phone} 
                  disabled={isOtpSent} 
                  onChange={handlePhoneChange} 
                  placeholder="10 Digits Mobile Number" 
                  bg="gray.50"
                  pl="4"
                />
              </FormControl>

              {isSignup ? (
                <FormControl isRequired>
                  <FormLabel fontSize="xs" fontWeight="bold" color="gray.600">Password</FormLabel>
                  <InputGroup>
                    <Input 
                      type={showPassword ? 'text' : 'password'} 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      placeholder="Enter your password" 
                      bg="gray.50"
                    />
                    <InputRightElement h={'full'}>
                      <Button
                        variant={'ghost'}
                        onClick={() => setShowPassword((showPassword) => !showPassword)}
                      >
                        {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
              ) : (
                loginMethod === 'password' ? (
                  <FormControl isRequired>
                    <FormLabel fontSize="xs" fontWeight="bold" color="gray.600">Password</FormLabel>
                    <InputGroup>
                      <Input 
                        type={showPassword ? 'text' : 'password'} 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        placeholder="Enter your password" 
                        bg="gray.50"
                      />
                      <InputRightElement h={'full'}>
                        <Button
                          variant={'ghost'}
                          onClick={() => setShowPassword((showPassword) => !showPassword)}
                        >
                          {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                ) : (
                  isOtpSent && (
                    <FormControl isRequired>
                      <FormLabel fontSize="xs" fontWeight="bold" color="gray.600" textAlign="center">Enter 4-Digit OTP</FormLabel>
                      <HStack justifyContent="center">
                        <PinInput 
                          otp 
                          type="number" 
                          value={otp} 
                          onChange={(value) => setOtp(value)}
                          focusBorderColor="teal.500"
                        >
                          <PinInputField bg="gray.100" />
                          <PinInputField bg="gray.100" />
                          <PinInputField bg="gray.100" />
                          <PinInputField bg="gray.100" />
                        </PinInput>
                      </HStack>
                    </FormControl>
                  )
                )
              )}

              <Button
                isLoading={loading}
                bg={'#0C727E'}
                color={'white'}
                fontWeight={500}
                type="submit"
                size="lg"
                mt={2}
                _hover={{ bg: '#095a63' }}
              >
                {isSignup ? 'Sign Up' : (loginMethod === 'password' ? 'Log In' : (isOtpSent ? 'Verify & Log In' : 'Send OTP'))}
              </Button>

              {!isSignup && (
                <Link 
                  textAlign="center" 
                  fontSize="sm" 
                  color="#0C727E" 
                  fontWeight="bold"
                  onClick={() => {
                    setLoginMethod(loginMethod === 'password' ? 'otp' : 'password');
                    setIsOtpSent(false);
                    setOtp('');
                  }}
                >
                  {loginMethod === 'password' ? 'Login with OTP instead' : 'Login with Password instead'}
                </Link>
              )}

              <Text textAlign="center" fontSize="sm" mt={0}>
                {isSignup ? 'Already have an account? ' : "Don't have an account? "}
                <Link color="#0C727E" fontWeight="bold" onClick={() => {
                  setIsSignup(!isSignup);
                  setLoginMethod('password');
                  setIsOtpSent(false);
                }}>
                  {isSignup ? 'Log In' : 'Sign Up'}
                </Link>
              </Text>
            </VStack>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
